import { useEffect, useState } from "react";
import { Menu, Globe, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { LANGS } from "@/i18n/translations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { SITE } from "@/data/site";

export const Logo = ({ testid = "header-logo" }) => (
  <a href="#top" data-testid={testid} className="flex items-center gap-2.5 group" aria-label="Hammer Gym Studios Lugano">
    <span className="relative w-10 h-10 bg-hammer-red border-2 border-bone grid place-items-center font-display text-[28px] text-bone leading-none pt-1 -rotate-3 group-hover:rotate-0 transition-transform duration-300">
      H
    </span>
    <span className="leading-none">
      <span className="block font-mono text-[8px] tracking-[0.3em] text-bone/60 whitespace-nowrap mb-[3px]">LUGANO · 2011</span>
      <span className="font-display text-[26px] text-bone tracking-wide">
        HAMMER<span className="text-hammer-red">GYM</span>
      </span>
    </span>
  </a>
);

export const LangSwitcher = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="language-switcher"
        className="flex items-center gap-1.5 h-10 px-3 border-2 border-bone/40 hover:border-bone text-bone font-mono text-xs tracking-widest uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-hammer-red"
      >
        <Globe className="w-4 h-4" />
        {lang.toUpperCase()}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-ink text-bone border-2 border-bone rounded-none min-w-[150px] z-[80]">
        {LANGS.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`language-option-${l.code}`}
            onClick={() => setLang(l.code)}
            className={`font-mono text-xs uppercase tracking-widest rounded-none cursor-pointer focus:bg-hammer-red focus:text-bone ${
              lang === l.code ? "text-hammer-red" : ""
            }`}
          >
            <span className="w-7">{l.short}</span> {compact ? "" : l.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const Header = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const links = [
    { href: "#palestra", label: t.nav.gym, id: "gym" },
    { href: "#attrezzatura", label: t.nav.equipment, id: "equipment" },
    { href: "#galleria", label: t.nav.gallery, id: "gallery" },
    { href: "#prezzi", label: t.nav.pricing, id: "pricing" },
    { href: "#contatti", label: t.nav.contact, id: "contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 bg-ink border-b-2 transition-[padding,border-color] duration-300 ${
        scrolled ? "border-hammer-red py-2" : "border-bone/10 py-3 sm:py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav-menu">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="link-slide font-mono text-[12px] uppercase tracking-[0.18em] text-bone/80 hover:text-bone pb-1"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <LangSwitcher />
          <span className="hidden sm:block">
            <a
              href="#prova"
              data-testid="header-free-trial-button"
              className="btn-brutal on-dark btn-red !text-lg !py-2.5 !px-4"
            >
              {t.nav.trial}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </span>
          <Sheet>
            <SheetTrigger
              data-testid="mobile-menu-button"
              aria-label={t.nav.menu}
              className="lg:hidden h-10 w-10 grid place-items-center border-2 border-bone text-bone"
            >
              <Menu className="w-5 h-5" />
            </SheetTrigger>
            <SheetContent side="right" className="bg-ink text-bone border-l-4 border-hammer-red w-[86vw] max-w-sm p-0">
              <SheetTitle className="sr-only">{t.nav.menu}</SheetTitle>
              <div className="p-6 pt-14 flex flex-col h-full">
                <p className="font-mono text-[11px] tracking-[0.3em] text-bone/50 mb-6">MENU / {SITE.city.toUpperCase()}</p>
                <nav className="flex flex-col" data-testid="mobile-nav-menu">
                  {[...links, { href: "#prova", label: t.nav.trial, id: "trial" }].map((l, i) => (
                    <SheetClose asChild key={l.id}>
                      <a
                        href={l.href}
                        data-testid={`mobile-nav-link-${l.id}`}
                        className="font-display text-5xl py-2 border-b border-bone/10 flex items-baseline gap-3 hover:text-hammer-red transition-colors"
                      >
                        <span className="font-mono text-xs text-hammer-red">0{i + 1}</span>
                        {l.label}
                      </a>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-auto pt-8 font-mono text-xs text-bone/60 space-y-1">
                  <p>{SITE.street}, {SITE.city}</p>
                  <a href={SITE.tel} className="block text-bone">{SITE.phoneDisplay}</a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
